// WS-75 life: world-space skinning on a node material, shared by the herds (instancing.js) and the people (lazy/folk.js).
// The bones already hold world skin matrices (bone world x inverse bind), one set per drawn instance, so the mesh's own
// and instance matrices stay identity. bone(i) -> mat4 node for skin index i (an int node) of the current instance.
// Colour pass: positionNode skins the (morphed) local position, and setupPosition then skins normalLocal, as three.js's
// own skinning does. Shadow pass: castShadowPositionNode (three.js's shadow pass reads that slot, not a depth material).
import * as THREE from 'three/webgpu';
import { Fn, attribute, add, vec4, int, positionLocal, normalLocal } from 'three/tsl';
import { usable } from './internals.js'; // VIS-194: three's private parts, checked at start-up

/** the blended skin matrix of the current vertex */
export function skinMatrix(bone) {
  const i = attribute('skinIndex', 'uvec4'), w = attribute('skinWeight', 'vec4');
  return add(w.x.mul(bone(int(i.x))), w.y.mul(bone(int(i.y))), w.z.mul(bone(int(i.z))), w.w.mul(bone(int(i.w))));
}

/**
 * Skin material `m` in world space. offset() -> vec3 added to the local position before the skin (colour pass only, e.g.
 * a rug standing proud of the coat); shadowAdd -> vec3 added after the skin in the shadow pass only (people's lift).
 * Its mesh sets userData.identityInstances (identityInstances below).
 */
export function skinWorld(m, bone, { offset = null, shadowAdd = null } = {}) {
  identityInstances(); // (before setupPosition is read below)
  m.positionNode = Fn(() => skinMatrix(bone).mul(vec4(offset ? positionLocal.add(offset()) : positionLocal, 1)).xyz)();
  m.castShadowPositionNode = Fn(() => { const p = skinMatrix(bone).mul(vec4(positionLocal, 1)).xyz; return shadowAdd ? p.add(shadowAdd) : p; })();
  const base = m.setupPosition;
  m.setupPosition = function (builder) {
    const out = base.call(this, builder);
    if (builder.geometry.hasAttribute('normal')) normalLocal.assign(skinMatrix(bone).mul(vec4(normalLocal, 0)).xyz);
    return out;
  };
  return m;
}

/**
 * An InstancedMesh whose instance matrices stay identity (userData.identityInstances: the herds and the people, skinned in
 * world space) builds every pass without three's instancing step, its colour pass and the shadow pass's own material
 * alike. main.js reads instance matrices as a vertex buffer, 4 inputs a mesh (+ `normal` in a shadow pass, read only by
 * that step), which took a horse herd to 15 inputs + gl_VertexID (morphs) + gl_InstanceID (its look): over the 16 of a
 * driver that counts those two (a Samsung phone: "Too many attributes (gl_VertexID)", so no horse was drawn, only its
 * tack). three's gate: an InstancedMesh with an instanceMatrix (gpu/internals.js 'NodeMaterial.setupPosition').
 */
export function identityInstances() {
  const NM = THREE.NodeMaterial.prototype;
  if (NM._visIdentity || !usable('NodeMaterial.setupPosition')) return;
  NM._visIdentity = true;
  const base = NM.setupPosition;
  NM.setupPosition = function (builder) {
    const o = builder.object, im = o?.isInstancedMesh && o.userData?.identityInstances ? o.instanceMatrix : null;
    if (!im) return base.call(this, builder);
    o.instanceMatrix = null;
    try { return base.call(this, builder); } finally { o.instanceMatrix = im; }
  };
}
/** The vertex inputs a program may use on every phone: 16, less gl_VertexID and gl_InstanceID where a driver counts them
 *  (tests/unit/runtime/life-inputs.test.mjs holds every moving-life program to it). */
export const PHONE_INPUTS = 16;
